import { Patrol } from '@local/common-lib';
import { Alert } from '@mui/material';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import uri from 'uri-tag';
import type { Issue } from 'validata';
import { CenterLayout } from '../../components/page-layout/index.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { formikCheck, issuesToFormik } from '../../utils/validation.js';
import type { EditPatrolData } from './edit.view.js';
import { EditView } from './edit.view.js';
import { ExistingPatrolPage } from './existing-patrol.js';

export const EditPatrolContent: FC<ExistingEntityViewProps<Patrol.Type>> = ({
  data: patrol,
  id,
}) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik<EditPatrolData>({
    initialValues: {
      hours: patrol.hours,
      instructions: patrol.instructions || '',
      name: patrol.name,
      patrol: patrol.type === 'patrol',
      when: patrol.when,
    },
    onSubmit: (values) => {
      const url = uri`/api/v1/patrol/${id}`;
      const body = {
        hours: values.hours,
        instructions: values.instructions || null,
        name: values.name,
        when: values.when,
      };
      return auth.axios
        .patch<{ inserted: Patrol.Type }>(url, body)
        .then(() => {
          enqueueSnackbar('Patrol updated', { variant: 'success' });
          navigate(-1);
          return undefined;
        })
        .catch((error: AxiosError<{ issues: Issue[] }>) => {
          console.log(error);
          Sentry.captureException(error);
          if (error.response && error.response.status === 400) {
            if ('issues' in error.response.data) {
              const issues = error.response.data.issues;
              formik.setErrors(issuesToFormik(issues));
              return;
            }
          }
          enqueueSnackbar('Unable to update patrol', { variant: 'error' });
        });
    },
    validate: (value) =>
      value.patrol
        ? formikCheck(Patrol.patchContract, {
            hours: value.hours,
            name: value.name,
            when: value.when,
          })
        : formikCheck(Patrol.createEventContract, {
            hours: value.hours,
            instructions: value.instructions || null,
            name: value.name,
            when: value.when,
          }),
  });

  const start = DateTime.fromJSDate(patrol.when);
  const now = DateTime.now();
  const futurePatrol = now < start;

  if (!futurePatrol) {
    return (
      <CenterLayout>
        <Alert severity="warning">Only future patrols may be edited.</Alert>
      </CenterLayout>
    );
  }

  if (patrol.started) {
    return (
      <CenterLayout>
        <Alert severity="warning">
          Patrols may not be edited once they have been started.
        </Alert>
      </CenterLayout>
    );
  }

  if (patrol.cancelled) {
    return (
      <CenterLayout>
        <Alert severity="warning">Cancelled patrols may not be edited.</Alert>
      </CenterLayout>
    );
  }

  return <EditView cancel={() => navigate(-1)} formik={formik} />;
};

export const EditPatrolPage: FC = () => (
  <ExistingPatrolPage view={EditPatrolContent} />
);
